import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Sparkles, ArrowRight } from "lucide-react";
import { useMist } from "@/mist";
import CrystalBall from "./CrystalBall";
import Marquee from "./Marquee";
import Manifesto from "./Manifesto";
import Footer from "./Footer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const FALLBACK = ["/visions/vision-1.jpg", "/visions/vision-2.jpg", "/visions/vision-3.jpg", "/visions/vision-4.jpg"];

const useVisions = () => {
  const [visions, setVisions] = useState(FALLBACK);
  useEffect(() => {
    axios
      .get(`${BACKEND_URL}/api/oracle/visions`, { params: { limit: 8 } })
      .then(({ data }) => {
        const real = (data.visions || []).map((u) => `${BACKEND_URL}${u}`);
        if (real.length) setVisions([...real, ...FALLBACK].slice(0, Math.max(4, real.length)));
      })
      .catch(() => {});
  }, []);
  return visions;
};

const titleLine = {
  hidden: { y: "115%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1.1, delay: 0.25 + i * 0.18, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const EnterButton = ({ t, testId }) => {
  const enterWithMist = useMist();
  return (
    <button
      onClick={() => enterWithMist("/oracle")}
      data-testid={testId}
      className="group flex items-center gap-3 rounded-full bg-amber-500 px-9 py-4 text-sm font-semibold uppercase tracking-[0.3em] text-[#030014] shadow-[0_0_50px_rgba(245,158,11,0.35)] transition-[background-color,box-shadow,transform] duration-400 hover:bg-amber-300 hover:shadow-[0_0_70px_rgba(245,158,11,0.5)] active:scale-95"
    >
      {t.enter}
      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
    </button>
  );
};

const Welcome = ({ t }) => {
  const visions = useVisions();
  return (
  <>
    <section
      data-testid="welcome-hero"
      className="relative z-10 flex flex-col items-center px-6 pb-2 pt-20 text-center"
    >
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.2, delay: 0.15 }}
        data-testid="welcome-eyebrow"
        className="mb-5 flex flex-wrap items-center justify-center gap-3 text-center text-xs font-medium uppercase tracking-[0.45em] text-amber-500/80"
      >
        <Sparkles className="h-4 w-4" strokeWidth={1.5} />
        {t.eyebrow}
      </motion.p>

      <h1 className="mb-6 text-center font-serif font-light leading-[1.02] tracking-tight text-white/95">
        <span className="block overflow-hidden">
          <motion.span className="block text-4xl sm:text-5xl lg:text-6xl" variants={titleLine} custom={0} initial="hidden" animate="visible">
            {t.titleA}
          </motion.span>
        </span>
        <span className="block overflow-hidden">
          <motion.span className="block text-4xl italic text-amber-400 sm:text-5xl lg:text-6xl" variants={titleLine} custom={1} initial="hidden" animate="visible">
            {t.titleB}
          </motion.span>
        </span>
      </h1>

      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1.2, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
        data-testid="welcome-mini-ball"
      >
        <CrystalBall phase="prompt" gallery={visions} size="w-[min(44vw,170px)]" />
      </motion.div>
    </section>

    <Manifesto t={t} />

    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.9, delay: 1.2 }}
      data-testid="welcome-cta-section"
      className="relative z-10 flex justify-center px-6 pb-24 pt-4"
    >
      <EnterButton t={t} testId="enter-oracle-button" />
    </motion.div>

    <Marquee t={t} />
    <Footer t={t} />
  </>
  );
};

export default Welcome;
